import type { MiddlewareApi } from '@types';
import { cordovaPrintPDF } from '../modules/context/cordova';
import { OPEN_TOAST, SHARE_TOAST_ID, closeToastById } from '../modules/context/toasts';
import { Api } from '../../helpers';
import { pdfToBase64, isPresent, isIOSApp } from '@helpers/helpers';
import sanitize from 'sanitize-filename';
import moment from 'moment';
import { extension } from 'mime-to-extensions';

const ACTION_TYPE = '__showPDF';

export const socialShareFile = (fileBlob: Blob, title, dispatch) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    if (!window.plugins || !window.plugins.socialsharing) {
      dispatch({
        type: OPEN_TOAST,
        data: {
          type: 'error', text: 'Compartilhamento não suportado', close: true,
        },
      });
      return;
    }

    const filename = sanitize(`${moment().unix()}-${title}`).toLowerCase().replace(/\.[^/.]+$/, '');
    // iOS names a PDF from a data URL "attachment.pdf" unless it comes as "df:<name>;<data URL>" (";" would end the name).
    // An attachment's title is its file name, already ending in ".pdf": dropped, so it isn't shared as "laudo.pdf.pdf"
    const file = isIOSApp() && fileBlob.type === 'application/pdf'
      ? `df:${sanitize(String(title || '').replace(/;/g, '')).trim().replace(/\.pdf$/i, '').trim() || moment().unix()}.pdf;${reader.result}`
      : reader.result;

    window.plugins.socialsharing.share(isPresent(title) ? title : `${moment().unix()}-file.${extension(fileBlob.type)}`, filename, file, null, () => {
      setTimeout(() => dispatch(closeToastById(SHARE_TOAST_ID)), 2000);
    }, () => {
      dispatch({
        type: OPEN_TOAST,
        data: {
          type: 'error', text: 'Compartilhamento falhou', close: true,
        },
      });
    });
  };
  reader.readAsDataURL(fileBlob);
};

const actionHandler = (dispatch, {
  apiRoute, title, prepare, success, error, sendEmail,
}) => {
  // prepare, success, error can be either functions or string representing the action type
  // this helper function makes it easier to call them.
  const callDispatcher = (fn, params) => {
    if (typeof fn === 'string') {
      dispatch({ type: fn, ...params });
    } else if (typeof fn === 'function') {
      fn(dispatch, params);
    }
  };


  callDispatcher(prepare);

  if (!window.isCordovaApp) {
    if (sendEmail) {
      dispatch({ type: OPEN_TOAST, data: { type: 'inProgress', text: 'Enviando PDF...' } });
    } else {
      dispatch({ type: OPEN_TOAST, data: { type: 'inProgress', text: 'Preparando PDF...' } });
    }
  }

  Api.get(apiRoute, {
    success: response => {
      const file = new Blob([response], { type: 'application/pdf' });

      if (window.isCordovaApp && ((!!window.plugins && !!window.plugins.socialsharing) || (!!window.plugins && !!window.plugins.PrintPDF))) {
        if (!!window.plugins && !!window.plugins.socialsharing) {
          // socialShareFile closes it (by id) when the share sheet is done; the timeout is in case it never reports back
          dispatch({
            type: OPEN_TOAST,
            data: {
              type: 'success', text: 'Compartilhando PDF', fileURL: undefined, close: true, timeout: 10000, id: SHARE_TOAST_ID,
            },
          });
          socialShareFile(file, title, dispatch);
          callDispatcher(success);
        } else {
          pdfToBase64(response)
            .then((base64: string) => {
              callDispatcher(success);
              dispatch(cordovaPrintPDF({ title, data: base64 }));
            });
          callDispatcher(success);
        }
      } else {
        const fileURL = URL.createObjectURL(file);
        callDispatcher(success);

        if (sendEmail) {
          dispatch({
            type: OPEN_TOAST,
            data: {
              type: 'success', text: 'PDF Enviado', fileURL, close: true,
            },
          });
        } else {
          dispatch({
            type: OPEN_TOAST,
            data: {
              type: 'success', text: 'PDF pronto', fileURL, close: true,
            },
          });
        }
      }
    },
    error: err => {
      callDispatcher(error, { message: err.message });
      dispatch({
        type: OPEN_TOAST,
        data: {
          type: 'error', text: 'Ocorreu um erro ao gerar PDF...', close: true,
        },
      });
    },
  });
};

export default function showPDF ({ dispatch }: MiddlewareApi) {
  return next => action => {
    if (action.type !== ACTION_TYPE) {
      return next(action);
    }
    actionHandler(dispatch, action);
  };
}

showPDF.action = ({
  apiRoute, prepare, success, error, sendEmail, socialShare, title,
}) => ({
  type: ACTION_TYPE, apiRoute, prepare, success, error, sendEmail, socialShare, title,
});

